import { AxiosError } from "axios";
import { useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { Button, SubmitButton } from "@/components/Interface/Button/Button";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import {
  stepDescriptionClassName,
  stepHeadingClassName,
} from "@/components/Portal/UserAccount/Security/AuthenticatorSetup/authenticator.utils";
import { NameConfirmationField } from "@/components/Portal/UserAccount/Security/AuthenticatorSetup/NameConfirmationField";
import { errorShouldBeRetried } from "@/components/Portal/UserAccount/Security/utils";
import { useAlert } from "@/hooks/useAlert";
import { useCancelTwoFactorSetup } from "@/hooks/useCancelTwoFactorSetup";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useChangeTwoFactorAuthentication } from "@/services/auth";
import { ChangeTwoFactorAuthenticationMethod } from "@/services/model";
import { AlertTypes } from "@/utils/atoms";

type ConfirmStandardForm = {
  name: string;
};

const paragraphKeys = ["text1", "text2", "text3"] as const;

export const TwoFactorConfirmStandard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<ConfirmStandardForm>({
    defaultValues: {
      name: "",
    },
    mode: "onChange",
  });
  const name = useWatch({ control, name: "name" });

  const { mutateAsync: cancelSetup, isPending: isCancelingSetup } =
    useCancelTwoFactorSetup();
  const { mutateAsync: changeTwoFactorAuth } =
    useChangeTwoFactorAuthentication();
  const showToast = useAlert();

  const isSubmitting = useRef(false);

  const onSubmit = () => {
    if (isSubmitting.current) {
      return;
    }
    isSubmitting.current = true;

    const handleSuccess = () => {
      showToast({
        text: t("portal:user-account.security.2fa.confirmStandard.success"),
        type: AlertTypes.success,
      });
      navigate(portalRoutes.cockpit.base);
    };

    const tryToChange = () =>
      changeTwoFactorAuth({
        params: { method: ChangeTwoFactorAuthenticationMethod.STANDARD },
      });

    tryToChange()
      .then(handleSuccess)
      .catch((error: AxiosError<any>) => {
        if (errorShouldBeRetried(error)) {
          cancelSetup().then(() => {
            setTimeout(() => tryToChange().then(handleSuccess), 5000);
          });
        } else {
          showToast({
            text: t("portal:user-account.security.2fa.confirmStandard.error"),
            type: AlertTypes.error,
          });
        }
      })
      .finally(() => {
        isSubmitting.current = false;
      });
  };

  return (
    <>
      {isCancelingSetup && <LoadingSpinner />}
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardModule
          stickyHeader
          title={t("portal:user-account.security.2fa.initialSetup.title")}
          actions={
            <>
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => navigate(-1)}
              >
                {t("Common.label.cancel")}
              </Button>
              <SubmitButton
                fullWidth={false}
                size="sm"
                disabled={isCancelingSetup || !name.trim()}
                label={t("common.buttons.confirm")}
              />
            </>
          }
        >
          <h3 className={stepHeadingClassName}>
            {t("portal:user-account.security.2fa.confirmStandard.subtitle")}
          </h3>
          {paragraphKeys.map((key) => (
            <p key={key} className={stepDescriptionClassName}>
              {t(`portal:user-account.security.2fa.confirmStandard.${key}`)}
            </p>
          ))}
          <NameConfirmationField
            register={register}
            errors={errors}
            title={t(
              "portal:user-account.security.2fa.confirmStandard.steps.2.title",
            )}
          />
        </CardModule>
      </form>
    </>
  );
};
